import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { UserPlus, Trash2, Shield, KeyRound, Store, Pencil } from "lucide-react";

const EMPTY = { name: "", email: "", password: "", role: "staff", branch_id: "", permissions: [] };

export default function Staff() {
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [permCatalog, setPermCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const [u, b, p] = await Promise.all([
      api.get("/users"),
      api.get("/branches"),
      api.get("/users/permissions").catch(() => ({ data: [] })),
    ]);
    setUsers(u.data);
    setBranches(b.data);
    setPermCatalog(p.data || []);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name || "",
      email: u.email || "",
      password: "",
      role: u.role || "staff",
      branch_id: u.branch_id || "",
      permissions: u.permissions || [],
    });
    setOpen(true);
  };

  const togglePerm = (key) => setForm((prev) => {
    const cur = new Set(prev.permissions || []);
    if (cur.has(key)) cur.delete(key); else cur.add(key);
    return { ...prev, permissions: [...cur] };
  });

  const submit = async () => {
    try {
      if (form.role === "staff" && !form.branch_id) return toast.error("Seleziona la sede dell'operatore");
      if (!editing && !form.password) return toast.error("Imposta una password");
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        delete payload.email; // email not editable
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("Operatore aggiornato");
      } else {
        await api.post("/users", form);
        toast.success("Operatore creato");
      }
      setOpen(false); setForm(EMPTY); setEditing(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggle = async (u) => {
    await api.patch(`/users/${u.id}`, { active: !u.active });
    toast.success(!u.active ? "Attivato" : "Disattivato"); load();
  };

  const remove = async (u) => {
    if (!window.confirm(`Eliminare ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Operatore rimosso"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "-";
  const isAdmin = form.role === "admin";

  return (
    <div className="space-y-6 animate-fade-up" data-testid="staff-page">
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <span className="pill pill-red">OPERATORI</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Chi lavora al negozio</h1>
          <p className="text-zinc-400 mt-1">Ogni operatore vede solo la sua sede. Ricontrolla i permessi prima di attivare.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="staff-new-button" onClick={openNew} className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] h-11">
              <UserPlus className="h-4 w-4 mr-2" /> Nuovo operatore
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-lg max-h-[90dvh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Modifica operatore" : "Nuovo operatore"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-300 mb-1 inline-block">Nome</Label>
                  <Input data-testid="staff-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-950 border-zinc-800" />
                </div>
                <div>
                  <Label className="text-zinc-300 mb-1 inline-block">Email</Label>
                  <Input data-testid="staff-form-email" type="email" disabled={!!editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-zinc-950 border-zinc-800 disabled:opacity-50" />
                </div>
              </div>
              <div>
                <Label className="text-zinc-300 mb-1 inline-block flex items-center gap-1"><KeyRound className="h-3 w-3" /> Password {editing && <span className="text-[10px] text-zinc-500 ml-1">(vuota = invariata)</span>}</Label>
                <Input data-testid="staff-form-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-zinc-950 border-zinc-800" placeholder="min. 6 caratteri" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-300 mb-1 inline-block">Ruolo</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, permissions: v === "admin" ? [] : form.permissions })}>
                    <SelectTrigger data-testid="staff-form-role" className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800">
                      <SelectItem value="staff">Operatore</SelectItem>
                      <SelectItem value="admin">Admin (tutti i permessi)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-300 mb-1 inline-block flex items-center gap-1"><Store className="h-3 w-3" /> Sede {form.role === "staff" && <span className="text-red-400">*</span>}</Label>
                  <Select value={form.branch_id || "__none__"} onValueChange={(v) => setForm({ ...form, branch_id: v === "__none__" ? "" : v })}>
                    <SelectTrigger data-testid="staff-form-branch" className="bg-zinc-950 border-zinc-800"><SelectValue placeholder="Seleziona sede" /></SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800">
                      {isAdmin && <SelectItem value="__none__">Tutte le sedi (admin)</SelectItem>}
                      {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {!isAdmin && (
                <div className="border border-zinc-800 rounded-xl p-3 bg-zinc-950" data-testid="staff-form-perms">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2 flex items-center gap-1">
                    <Shield className="h-3 w-3" /> Permessi ({form.permissions.length}/{permCatalog.length})
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {permCatalog.map((p) => (
                      <label key={p.key} className="flex items-center gap-2 p-1.5 rounded hover:bg-zinc-900 cursor-pointer" data-testid={`staff-perm-${p.key}`}>
                        <Checkbox checked={form.permissions.includes(p.key)} onCheckedChange={() => togglePerm(p.key)} />
                        <span className="text-xs text-zinc-300">{p.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <Button data-testid="staff-form-submit" onClick={submit} className="w-full bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
                {editing ? "Salva modifiche" : "Crea operatore"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-elev overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
            <tr className="text-left">
              <th className="p-4">Nome</th>
              <th className="p-4">Email</th>
              <th className="p-4">Ruolo</th>
              <th className="p-4">Sede</th>
              <th className="p-4">Permessi</th>
              <th className="p-4">Attivo</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-zinc-900" data-testid={`staff-row-${u.id}`}>
                <td className="p-4 font-medium">{u.name}</td>
                <td className="p-4 text-zinc-400">{u.email}</td>
                <td className="p-4">
                  <span className={`pill ${u.role === "admin" ? "pill-red" : "pill-muted"}`}>
                    {u.role === "admin" && <Shield className="h-3 w-3" />}{u.role}
                  </span>
                </td>
                <td className="p-4 text-zinc-400 text-xs">{u.branch_id ? branchName(u.branch_id) : (u.role === "admin" ? "Tutte" : "—")}</td>
                <td className="p-4 text-xs text-zinc-400">
                  {u.role === "admin" ? "Tutti" : `${(u.permissions || []).length}/${permCatalog.length}`}
                </td>
                <td className="p-4"><Switch data-testid={`staff-toggle-${u.id}`} checked={u.active} onCheckedChange={() => toggle(u)} /></td>
                <td className="p-4 text-right whitespace-nowrap">
                  <button data-testid={`staff-edit-${u.id}`} onClick={() => openEdit(u)} className="p-2 rounded-md text-zinc-500 hover:text-amber-300 hover:bg-amber-500/10">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button data-testid={`staff-delete-${u.id}`} onClick={() => remove(u)} className="p-2 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
